import React, { useMemo } from "react";

import {
  faEye,
  faPage,
  faPlay,
  faPlus,
  faWarning,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";
import { isAfter } from "date-fns";
import { twJoin } from "tailwind-merge";

import { Button } from "src/base-components/Button";
import { Icon } from "src/base-components/Icon";
import { Pill } from "src/base-components/Pill";
import {
  TableComp as Table,
  Cell as TableCell,
  Header as TableHeader,
} from "src/base-components/Table";
import { EmptyState } from "src/design-system/EmptyState";
import { IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { JobRun, JobRunStatusType } from "src/jobs/types";
import { SimulationStatusIndicator } from "src/simulations/SimulationStatusIndicator";
import { formatDate } from "src/utils/datetime";

const columnTextClassName = "text-gray-800 font-inter-medium-13px";
const datasetColumnSize = 220;
const helper = createColumnHelper<JobRun>();

const canLoadTestRun = (jobRun: JobRun) => {
  return [
    JobRunStatusType.Completed,
    JobRunStatusType.Running,
    JobRunStatusType.Writing,
  ].includes(jobRun.status.type);
};

const getColumns = () => [
  helper.display({
    id: "dataset",
    header: (info) => (
      <TableHeader dataLoc="test-run-dataset-header" info={info}>
        <span className={twJoin("px-4", columnTextClassName)}>Test data</span>
      </TableHeader>
    ),
    cell: (info) => (
      <TableCell className="mx-3.5" info={info}>
        <div
          className="flex h-full w-full items-center"
          data-loc={info.row.original.id}
        >
          <div className="flex items-center">
            <Icon color="text-gray-400" icon={faPage} size="xs" />
            <div className="min-w-0 flex-1 truncate">
              <span className="font-inter-medium-12px font-medium text-gray-700">
                {info.table.options.meta?.actions?.getDatasetNameBySourceId(
                  info.row.original.source_id,
                ) || "Unknown Dataset"}
              </span>
            </div>
          </div>
        </div>
      </TableCell>
    ),
    size: datasetColumnSize,
    minSize: datasetColumnSize,
    maxSize: datasetColumnSize,
  }),
  helper.display({
    id: "results",
    header: (info) => (
      <TableHeader dataLoc="results-header" info={info}>
        <span className={columnTextClassName}>Results</span>
      </TableHeader>
    ),
    cell: (info) => (
      <TableCell info={info}>
        <div className="flex h-full w-full items-center justify-between gap-x-6">
          <div className="flex-1">
            <SimulationStatusIndicator
              stoppedLabel="Test run stopped"
              success_rows_count={info.row.original.success_rows_count}
              failed_rows_count={info.row.original.failed_rows_count}
              total_rows_count={info.row.original.total_rows_count}
              ignored_rows_count={info.row.original.ignored_rows_count}
              status={info.row.original.status}
              footer={getFooter(
                info.table.options.meta?.actions?.getUsernameById(
                  "triggered_by" in info.row.original.trigger
                    ? info.row.original.trigger.triggered_by
                    : null,
                ) ?? null,
                info.row.original.created_at,
              )}
            />
          </div>
          <div
            className={twJoin(
              "flex items-center gap-x-2 group-hover:opacity-100",
              info.table.options.meta?.actions?.isLoaded(info.row.original.id)
                ? "opacity-100"
                : "opacity-0",
            )}
            data-loc="test-run-row-buttons-container"
          >
            {canLoadTestRun(info.row.original) && (
              <IconButton
                icon={faEye}
                onClick={() => {
                  const row = info.row.original;
                  const isLoaded = info.table.options.meta?.actions?.isLoaded(
                    row.id,
                  );
                  if (!isLoaded) {
                    info.table.options.meta?.actions?.loadTestRun(
                      info.row.original,
                    );
                  }
                }}
                size="xs"
              />
            )}

            {info.table.options.meta?.actions?.isOutdatedTestRun(
              info.row.original,
            ) ? (
              <Tooltip
                title="Incompatible test run"
                placement="right"
                body="This decision flow was changed after this test run. You might see incomplete results."
                asChild
              >
                <Pill variant="yellow" size="sm">
                  <Pill.Icon icon={faWarning} />
                </Pill>
              </Tooltip>
            ) : (
              <div className="w-7" />
            )}

            {isTestRunRunning(info.row.original) && (
              <IconButton
                icon={faXmark}
                onClick={() => {
                  info.table.options.meta?.actions?.stopTestRun(
                    info.row.original,
                  );
                }}
                size="xs"
              />
            )}
          </div>
        </div>
      </TableCell>
    ),
  }),
];

export const TestRunsTable: React.FC<{
  lastUpdatedAt: string;
  loadedTestRunId: string | null;
  jobRuns: JobRun[];
  datasetNamesBySourceId: Record<string, string>;
  usernameById: Record<string, string | null>;
  isLoading: boolean;
  isEmpty: boolean;
  onCreate: () => void;
  onStop: (jobRun: JobRun) => void;
  onLoad: (jobRun: JobRun) => void;
}> = ({
  datasetNamesBySourceId,
  loadedTestRunId,
  usernameById,
  isLoading,
  isEmpty,
  jobRuns,
  lastUpdatedAt,
  onCreate,
  onStop,
  onLoad,
}) => {
  const columns = useMemo(() => getColumns(), []);

  if (isLoading || !isEmpty) {
    return (
      <div className="bg-white">
        <Table
          columns={columns}
          data={jobRuns}
          dataLoc="test-runs-table"
          frameClassName="h-auto border-gray-100 border-b"
          isLoading={isLoading}
          noScroll={true}
          rowClassName="group hover:bg-gray-50"
          actions={{
            getDatasetNameBySourceId: (sourceId: string) => {
              return datasetNamesBySourceId[sourceId];
            },
            getUsernameById: (userId: string) => {
              return usernameById[userId];
            },
            isOutdatedTestRun: (testRun: JobRun) => {
              return isAfter(lastUpdatedAt, testRun.created_at);
            },
            stopTestRun: onStop,
            loadTestRun: onLoad,
            isLoaded: (testRunId: string) => {
              return testRunId === loadedTestRunId;
            },
          }}
        />
      </div>
    );
  }

  return (
    <EmptyState
      action={
        <Button
          dataLoc="test-runs-create-test-run-button"
          iconLeft={faPlus}
          size="sm"
          variant="secondary"
          onClick={onCreate}
        >
          New test run
        </Button>
      }
      description="Create your first test run"
      headline="No test runs available"
      icon={faPlay}
    />
  );
};

const isTestRunRunning = (jobRun: JobRun) => {
  return jobRun.status.type === JobRunStatusType.Running;
};

const getFooter = (username: string | null, createdAt: string) => {
  if (username) {
    return `Test started by ${username} on ${formatDate(createdAt)}`;
  }

  return `Test started on ${formatDate(createdAt)}`;
};
